import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ItemStatus } from "@focus/focusUI";
import { catchError, map, mergeMap, of } from "rxjs";
import { ofType } from "redux-observable";
import { createAsyncReducers } from "./helpers";
import type { EventEvolutionEvent } from "../components/events/EventEvolutionTypes";

const baseURL = "/api/v1";

export type eventsEvolutionRequestKey = "getEvent";

type RequestState = {
  status: ItemStatus;
  error: string | null;
};

type RequestsMap = Record<eventsEvolutionRequestKey, RequestState>;

type EventsEvolutionPayload = {
  event?: EventEvolutionEvent;
};

type EventsEvolutionState = EventsEvolutionPayload & {
  requests: RequestsMap;
};

const initialState: EventsEvolutionState = {
  event: undefined,
  requests: {
    getEvent: { status: ItemStatus.PENDING, error: null },
  },
};

const getEventsEvolutionAsync = createAsyncReducers<
  EventsEvolutionState,
  eventsEvolutionRequestKey,
  EventEvolutionEvent,
  { id: string }
>("getEvent", (state, action) => {
  state.event = action.payload;
});

const eventsEvolutionSlice = createSlice({
  name: "events-evolution",
  initialState,
  reducers: {
    getEventsEvolutionRequest: getEventsEvolutionAsync.request,
    getEventsEvolutionSuccess: getEventsEvolutionAsync.success,
    getEventsEvolutionFailure: getEventsEvolutionAsync.failure,
  },
});

export const {
  getEventsEvolutionRequest,
  getEventsEvolutionSuccess,
  getEventsEvolutionFailure,
} = eventsEvolutionSlice.actions;

const getEventsEvolutionEpic = (action$, _state$, { ajax }) =>
  action$.pipe(
    ofType(getEventsEvolutionRequest.type),
    mergeMap((action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const url = `${baseURL}/events/${id}`;
      return ajax.getJSON(url).pipe(
        map((object: EventEvolutionEvent) => getEventsEvolutionSuccess(object)),
        catchError((error) =>
          of(getEventsEvolutionFailure(error.message || "Could not load event.")),
        ),
      );
    }),
  );

export default {
  actions: eventsEvolutionSlice.actions,
  reducer: eventsEvolutionSlice.reducer,
  middleware: [getEventsEvolutionEpic],
  selectors: eventsEvolutionSlice.selectors,
};
